import { Tab, TabList, TabPanel, TabPanels, Tabs, Td, Text } from "@chakra-ui/react";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiShoppingCart } from "react-icons/fi";
import {
  SortDirection,
  SourcingEventSortField,
  useListBuyerEventsQuery,
  useListMyRecentlyViewedSourcingEventsQuery,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { momentLocaleFor } from "../../../common/formatting/i18nFormatters";
import { bucketQuery } from "../../../common/layout/statusBuckets";
import { urls } from "../../../urls";
import { EventListItemStatusTag } from "../../sourcing-event/EventListItemStatusTag";
import { ACTIVE_EVENT_STATUSES, DRAFT_EVENT_STATUSES, isDeadlineSoon } from "../../sourcing-events/eventStatusFilters";
import { useHomeLabel } from "../useHomeLabel";
import { PanelBody, PanelCard, PanelHeader } from "./HomePanel";
import { MutedCell, PanelRow, PanelTable, TitleCell } from "./PanelTable";

const ACTIVE_LIMIT = 6;
const DRAFT_LIMIT = 5;
const RECENT_LIMIT = 5;

/**
 * Everything about procurements in one card. Three tables that each answered a different question
 * about the same thing were three cards competing for the same corner of the dashboard; as tabs they
 * read as one subject, and the eye lands on the one that matters today.
 *
 * All three load up front so switching tabs is instant — the same three requests the separate cards
 * made, just arranged differently.
 */
export const SourcingEventsPanel = () => {
  const { t, i18n } = useTranslation();
  const homeLabel = useHomeLabel();
  const displayDate = useDisplayDate();
  const displayDateWithTime = useDisplayDateWithTime();
  const authState = useLoggedInWithOrgContextState();
  const momentLocale = momentLocaleFor(i18n.language);
  const [tabIndex, setTabIndex] = useState(0);

  // The card speaks for the organization — its title, its phase bar, and the list its "see all"
  // opens all do. Tabs filtered to the reader would put two different draft counts in one card,
  // inches apart. "Recently opened" stays personal because only a person opens anything.
  const active = useListBuyerEventsQuery({
    orgId: authState.selectedOrg.id,
    statuses: ACTIVE_EVENT_STATUSES,
    sortBy: SourcingEventSortField.ModifiedAt,
    sortDirection: SortDirection.Desc,
    limit: ACTIVE_LIMIT,
  });
  const drafts = useListBuyerEventsQuery({
    orgId: authState.selectedOrg.id,
    statuses: DRAFT_EVENT_STATUSES,
    sortBy: SourcingEventSortField.ModifiedAt,
    sortDirection: SortDirection.Desc,
    limit: DRAFT_LIMIT,
  });
  const recent = useListMyRecentlyViewedSourcingEventsQuery({ limit: RECENT_LIMIT });

  const activeEvents = active.data?.events ?? [];
  const draftEvents = drafts.data?.events ?? [];
  const recentEvents = recent.data?.events ?? [];

  const seeAllTo = [
    urls.app.sourcingEvents.viewAll,
    urls.app.sourcingEvents.viewAll,
    `${urls.app.sourcingEvents.viewAll}?${bucketQuery("arbeid")}`,
  ][tabIndex];

  return (
    <PanelCard>
      <PanelHeader titleKey="Procurements" icon={FiShoppingCart} seeAllTo={seeAllTo} />
      <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="teal" size="sm" isLazy={false}>
        <TabList px="2" borderBottom="1px solid" borderColor="smBorder">
          <Tab>{homeLabel("Recently opened")}</Tab>
          <Tab>
            {homeLabel("Ongoing")}
            {!!active.data?.count && <Count value={active.data.count} />}
          </Tab>
          <Tab>
            {homeLabel("Drafts")}
            {!!drafts.data?.count && <Count value={drafts.data.count} />}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel p="0">
            <PanelBody
              isLoading={recent.isLoading}
              isError={recent.isError}
              isEmpty={recentEvents.length === 0}
              emptyKey="Open a procurement and it shows up here"
            >
              <PanelTable headers={[t("Title"), t("Status"), t("Last opened")]}>
                {recentEvents.map(({ event, viewedAt }) => (
                  <PanelRow key={event.id} to={urls.app.sourcingEvents.view.go(event.id)}>
                    <TitleCell title={event.title || t("Untitled")} />
                    <Td>
                      <EventListItemStatusTag event={event} size="sm" />
                    </Td>
                    <MutedCell>{moment(viewedAt).locale(momentLocale).fromNow()}</MutedCell>
                  </PanelRow>
                ))}
              </PanelTable>
            </PanelBody>
          </TabPanel>

          <TabPanel p="0">
            <PanelBody
              isLoading={active.isLoading}
              isError={active.isError}
              isEmpty={activeEvents.length === 0}
              emptyKey="No active procurements yet"
            >
              <PanelTable headers={[t("Title"), t("Status"), t("Deadline")]}>
                {activeEvents.map((event) => {
                  const soon = isDeadlineSoon(event.deadline);
                  return (
                    <PanelRow key={event.id} to={urls.app.sourcingEvents.view.go(event.id)}>
                      <TitleCell title={event.title || t("Untitled")} />
                      <Td>
                        <EventListItemStatusTag event={event} size="sm" />
                      </Td>
                      <Td>
                        <Text fontSize="sm" color={soon ? "smError" : "smMuted"}>
                          {event.deadline ? displayDateWithTime(event.deadline) : "—"}
                        </Text>
                      </Td>
                    </PanelRow>
                  );
                })}
              </PanelTable>
            </PanelBody>
          </TabPanel>

          <TabPanel p="0">
            <PanelBody
              isLoading={drafts.isLoading}
              isError={drafts.isError}
              isEmpty={draftEvents.length === 0}
              emptyKey="No drafts"
            >
              <PanelTable headers={[t("Title"), t("Last edited")]}>
                {draftEvents.map((event) => (
                  <PanelRow key={event.id} to={urls.app.sourcingEvents.view.go(event.id)}>
                    <TitleCell title={event.title || t("Untitled")} />
                    <MutedCell>{displayDate(event.modifiedAt)}</MutedCell>
                  </PanelRow>
                ))}
              </PanelTable>
            </PanelBody>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </PanelCard>
  );
};

const Count = ({ value }: { value: number }) => (
  <Text as="span" ml="1.5" fontSize="xs" color="smMuted">
    {value}
  </Text>
);
